import React from "react";
import { Linking, Platform, Pressable, View } from "react-native";
import StyledText from "../../../common/ui/styled-text";
import {
  PaymentMethodIcon,
  extractPaymentMethodDetails,
} from "../payment-method";
import type {
  StripePaymentMethod,
  StripeInvoice,
} from "../../../../types/api/billing";
import { ExternalLink } from "lucide-react-native";
import { displayPrice } from "../../../../utils/price-helpers";
import dayjs from "../../../../utils/dayjs";
import clsx from "clsx";

interface BillingHistoryItem {
  label: string; // e.g., "Dec 2024"
  amount: string; // e.g., "$122.50"
  paymentMethod?: StripePaymentMethod | null;
  status?: string | null; // raw invoice status string (e.g., 'paid', 'open')
  invoiceUrl?: string | null; // direct link to invoice
}

interface BillingHistoryProps {
  invoices: StripeInvoice[];
}

const BillingHistory: React.FC<BillingHistoryProps> = ({ invoices }) => {
  const list: BillingHistoryItem[] = (invoices ?? [])
    .slice()
    .sort((a, b) => (b?.created || 0) - (a?.created || 0))
    .map((inv) => {
      const created = inv?.created ? dayjs.unix(inv.created) : null;
      const label = created ? created.format("MMM YYYY") : inv?.number || "—";
      const amount =
        typeof inv?.total === "number" ? displayPrice(inv.total) : "—";
      // Prefer the payment method used by the invoice's payment intent, else fall back to the invoice's default payment method
      let paymentMethod =
        inv?.payment_intent?.payment_method ??
        inv?.default_payment_method ??
        null;

      return {
        label,
        amount,
        paymentMethod,
        status: inv?.status || null,
        invoiceUrl: inv?.invoice_pdf || null,
      };
    });
  return (
    <View className="rounded-lg border border-gray-200 bg-white p-4">
      <StyledText className="text-sm font-medium text-gray-900 mb-3">
        Billing History
      </StyledText>
      {list.length === 0 ? (
        <StyledText className="text-sm text-gray-500">
          No invoices yet
        </StyledText>
      ) : (
        <View>
          <View className="flex-row items-center pb-2 space-x-1 sm:space-x-2 border-b border-gray-200 mb-1">
            <View className="min-w-[80px] basis-[120px] shrink">
              <StyledText className="text-[11px] text-gray-600">
                Invoice
              </StyledText>
            </View>
            <View className="w-[60px]" />
            <View className="flex-1" />
            <View className="min-w-[80px] basis-[120px] shrink items-end">
              <StyledText className="text-[11px] text-gray-600 text-center">
                Payment Method
              </StyledText>
            </View>
            <View className="min-w-[70px] basis-[100px] shrink items-end">
              <StyledText className="text-[11px] text-gray-600">
                Amount
              </StyledText>
            </View>
            <View className="w-[28px]" />
          </View>
          {list.map((it, idx) => (
            <View
              key={`${it.label}-${idx}`}
              className={`flex-row items-center py-2 space-x-1 sm:space-x-2 ${idx < list.length - 1 ? "border-b border-gray-200" : ""}`}
            >
              {/* Invoice name */}
              <View className="min-w-[80px] basis-[120px] shrink">
                <StyledText className="text-sm text-gray-700" numberOfLines={1}>
                  {it.label}
                </StyledText>
              </View>

              {/* Status column */}
              <View className="w-[60px] items-center">
                {it.status
                  ? (() => {
                      const s = (it.status || "").toLowerCase();
                      const isPaid = s === "paid";
                      const isOpen = s === "open";
                      const isUncollectible = s === "uncollectible";
                      const isVoid = s === "void";
                      const isDraft = s === "draft";
                      const badgeClass = clsx(
                        "px-2 py-[2px] rounded-full border",
                        {
                          "bg-green-50 border-green-200": isPaid,
                          "bg-red-50 border-red-200": isUncollectible,
                          "bg-yellow-50 border-yellow-200": isOpen,
                          "bg-gray-50 border-gray-200":
                            isVoid ||
                            isDraft ||
                            (!isPaid && !isOpen && !isUncollectible),
                        }
                      );
                      const textClass = clsx("text-[11px] font-medium", {
                        "text-green-700": isPaid,
                        "text-red-700": isUncollectible,
                        "text-yellow-700": isOpen,
                        "text-gray-700": !(isPaid || isOpen || isUncollectible),
                      });
                      return (
                        <View className={badgeClass}>
                          <StyledText className={textClass}>
                            {it.status}
                          </StyledText>
                        </View>
                      );
                    })()
                  : null}
              </View>
              <View className="flex-1" />

              {/* Payment Method */}
              <View className="min-w-[80px] basis-[120px] shrink items-end">
                {(() => {
                  const pm = it.paymentMethod;
                  if (!pm) return <View />;
                  if (typeof pm === "string") {
                    return (
                      <View className="flex-row items-center gap-1">
                        <PaymentMethodIcon type={"unknown"} color="#111" />
                        <StyledText
                          className="text-xs text-gray-500"
                          numberOfLines={1}
                        >
                          {pm}
                        </StyledText>
                      </View>
                    );
                  }
                  const { shortDisplay } = extractPaymentMethodDetails(
                    pm ?? undefined
                  );
                  return (
                    <View className="flex-row items-center gap-1">
                      <PaymentMethodIcon
                        type={pm.type || "unknown"}
                        color="#111"
                      />
                      <StyledText
                        className="text-xs text-gray-500"
                        numberOfLines={1}
                      >
                        {shortDisplay || "—"}
                      </StyledText>
                    </View>
                  );
                })()}
              </View>

              {/* Amount */}
              <View className="min-w-[70px] basis-[100px] shrink items-end">
                <StyledText className="text-sm text-gray-900">
                  {it.amount}
                </StyledText>
              </View>

              {/* Invoice PDF link (icon) */}
              <View className="w-[28px] items-end">
                {it.invoiceUrl ? (
                  <Pressable
                    accessibilityLabel="Open invoice"
                    onPress={() => {
                      const url = it.invoiceUrl as string;
                      if (Platform.OS === "web") {
                        if (typeof window !== "undefined") {
                          try {
                            window.open(url, "_blank", "noopener,noreferrer");
                          } catch {
                            // no-op
                          }
                        }
                      } else {
                        Linking.openURL(url).catch(() => {});
                      }
                    }}
                    className="p-1 rounded hover:bg-gray-100"
                  >
                    <ExternalLink size={16} color="#111" />
                  </Pressable>
                ) : (
                  <View />
                )}
              </View>
            </View>
          ))}
        </View>
      )}
    </View>
  );
};

export default BillingHistory;
